import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../components/app/card/card';
import { ProgressBar } from '../../../components/app/progress-bar/progress-bar';
import { DashboardReturnMetricSwitcher } from '../../../components/app/dashboard/return-metric/dashboard-return-metric-switcher';
import { useReturnMetricColors } from '../../../components/app/dashboard/return-metric/dashboard-return-metric';
import { IconBell } from '../../../components/app/icons/bell';
import { ReturnMetricEnum } from '../../../models/enums/return-metric-enum';

export type FeatureKey = 'performance' | 'dividends' | 'allocation' | 'tax' | 'watchlist' | 'alerts';

interface FeatureDemoProps {
    feature: FeatureKey;
}

const panel = 'flex flex-1 flex-col rounded-large border border-border bg-surface p-small shadow-card sm:p-medium';
const caption = 'text-vsmall font-bold uppercase tracking-wide text-text-muted';

const money = (value: number, currency = 'Kč') => `${new Intl.NumberFormat('cs-CZ').format(value)} ${currency}`;

const Metric: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
    <Card radius="large" shadow="medium" padding="small">
        <div>
            <p className={caption}>{label}</p>
            <p
                className={`mt-xsmall text-large font-bold sm:text-[2.8rem] sm:leading-tight ${
                    accent ? 'text-status-positive' : 'text-text'
                }`}
            >
                {value}
            </p>
        </div>
    </Card>
);

const PerformanceDemo: React.FC = () => {
    const { t } = useTranslation();
    const [metric, setMetric] = React.useState<ReturnMetricEnum>(ReturnMetricEnum.TimeWeightedReturn);
    const metricColors = useReturnMetricColors();

    return (
        <div className="flex flex-1 flex-col gap-small">
            <div className="grid grid-cols-2 gap-vsmall">
                <Metric label={t('pages.homepage.features.demo.performance.value')} value={money(1245800)} />
                <Metric
                    label={t('pages.homepage.features.demo.performance.return')}
                    value={metric === ReturnMetricEnum.TimeWeightedReturn ? '+12,4 %' : '+10,8 %'}
                    accent
                />
            </div>
            <div className={panel}>
                <div className="flex flex-wrap items-center justify-between gap-vsmall">
                    <div>
                        <p className={caption}>{t('pages.homepage.features.demo.performance.chart')}</p>
                        <p className="mt-xxsmall text-vsmall text-text-muted">
                            {t('pages.homepage.features.demo.performance.period')}
                        </p>
                    </div>
                    <DashboardReturnMetricSwitcher
                        value={metric}
                        onChange={setMetric}
                        metrics={[ReturnMetricEnum.SimpleReturn, ReturnMetricEnum.TimeWeightedReturn]}
                        size="small"
                    />
                </div>
                <svg
                    viewBox="0 0 600 205"
                    role="img"
                    aria-label={t('pages.homepage.features.demo.performance.chartAlt')}
                    className="mt-small min-h-[18rem] w-full flex-1 overflow-visible"
                >
                    <path d="M0 35H600M0 90H600M0 145H600M0 200H600" stroke="currentColor" className="text-border" />
                    <path
                        d="M0 163C65 170 85 147 145 154S245 135 310 127 412 97 470 111 540 80 600 77"
                        fill="none"
                        stroke="#FF697B"
                        strokeWidth="3"
                        strokeDasharray="6 6"
                    />
                    <path
                        d={
                            metric === ReturnMetricEnum.TimeWeightedReturn
                                ? 'M0 177C55 166 77 180 128 148S208 154 263 119 341 131 392 89 483 111 529 68 580 60 600 43'
                                : 'M0 180C55 174 78 183 128 154S205 161 263 132 341 139 392 104 479 121 529 82 579 77 600 63'
                        }
                        fill="none"
                        stroke={metricColors[metric]}
                        strokeWidth="4"
                        strokeLinecap="round"
                    />
                </svg>
                <div className="mt-vsmall flex flex-wrap gap-small text-vsmall text-text-muted">
                    <span>
                        <span
                            className="mr-xxsmall inline-block h-[0.8rem] w-[0.8rem] rounded-full"
                            style={{ backgroundColor: metricColors[metric] }}
                        />
                        {t('pages.homepage.features.demo.performance.portfolio')} ·{' '}
                        {metric === ReturnMetricEnum.TimeWeightedReturn ? 'TWR' : 'SR'}
                    </span>
                    <span>
                        <span className="mr-xxsmall inline-block h-[0.8rem] w-[0.8rem] rounded-full bg-status-negative" />
                        CSPX
                    </span>
                </div>
            </div>
        </div>
    );
};

const DividendsDemo: React.FC = () => {
    const { t } = useTranslation();
    const bars = [26, 43, 34, 56, 39, 70, 48, 64, 57, 83, 63, 77];

    return (
        <div className="flex flex-1 flex-col gap-small">
            <div className="grid grid-cols-2 gap-vsmall">
                <Metric label={t('pages.homepage.features.demo.dividends.received')} value={money(18420)} />
                <Metric label={t('pages.homepage.features.demo.dividends.yield')} value="2,8 %" accent />
            </div>
            <div className={panel}>
                <div className="flex flex-wrap items-baseline justify-between gap-vsmall">
                    <p className={caption}>{t('pages.homepage.features.demo.dividends.monthly')}</p>
                    <span className="text-vsmall text-text-muted">
                        {t('pages.homepage.features.demo.dividends.year')}
                    </span>
                </div>
                <div
                    role="img"
                    aria-label={t('pages.homepage.features.demo.dividends.chartAlt')}
                    className="relative mt-medium min-h-[18rem] flex-1 border-b border-border"
                >
                    <div className="absolute inset-0 flex items-end gap-[0.5rem] sm:gap-vsmall">
                        {bars.map((height, index) => (
                            <span
                                key={index}
                                className="min-w-0 flex-1 rounded-t-small bg-status-positive"
                                style={{ height: `${height}%` }}
                            />
                        ))}
                    </div>
                </div>
                <div className="mt-vsmall flex justify-between text-vsmall text-text-muted">
                    <span>{t('pages.homepage.features.demo.dividends.january')}</span>
                    <span>{t('pages.homepage.features.demo.dividends.june')}</span>
                    <span>{t('pages.homepage.features.demo.dividends.december')}</span>
                </div>
            </div>
        </div>
    );
};

const AllocationDemo: React.FC = () => {
    const { t } = useTranslation();
    const rows = [
        { label: t('pages.homepage.features.demo.allocation.stocks'), value: 56, color: '#3347FF' },
        { label: t('pages.homepage.features.demo.allocation.etf'), value: 27, color: '#FFA733' },
        { label: t('pages.homepage.features.demo.allocation.cash'), value: 17, color: '#865EF8' },
    ];

    return (
        <div className={panel}>
            <p className={caption}>{t('pages.homepage.features.demo.allocation.breakdown')}</p>
            <div className="mt-medium grid flex-1 items-center gap-medium sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <div
                    role="img"
                    aria-label={t('pages.homepage.features.demo.allocation.chartAlt')}
                    className="relative mx-auto aspect-square w-full max-w-[26rem] rounded-full"
                    style={{ background: 'conic-gradient(#3347FF 0 56%, #FFA733 56% 83%, #865EF8 83% 100%)' }}
                >
                    <div className="absolute inset-[25%] flex items-center justify-center rounded-full bg-surface text-center text-vsmall font-bold text-text">
                        {t('pages.homepage.features.demo.allocation.center')}
                    </div>
                </div>
                <div className="space-y-medium">
                    {rows.map((row) => (
                        <div
                            key={row.label}
                            className="flex items-center justify-between gap-small border-b border-border pb-vsmall text-small"
                        >
                            <span className="flex items-center gap-vsmall text-text-muted">
                                <span
                                    className="h-[1rem] w-[1rem] shrink-0 rounded-full"
                                    style={{ backgroundColor: row.color }}
                                />
                                {row.label}
                            </span>
                            <strong className="text-large text-text sm:text-[2.4rem]">{row.value} %</strong>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const TaxDemo: React.FC = () => {
    const { t } = useTranslation();
    return (
        <div className="flex flex-1 flex-col gap-small">
            <div className={panel}>
                <div className="flex items-center justify-between gap-small">
                    <p className={caption}>{t('pages.homepage.features.demo.tax.records')}</p>
                    <strong className="text-small text-text">2025</strong>
                </div>
                <div className="mt-small flex flex-1 flex-col justify-evenly gap-small">
                    <div className="flex items-center justify-between gap-small border-b border-border pb-small text-small">
                        <span className="text-text-muted">{t('pages.homepage.features.demo.tax.gainsLosses')}</span>
                        <strong className="text-large sm:text-[2.8rem]">{money(32780)}</strong>
                    </div>
                    <div className="flex items-center justify-between gap-small text-small">
                        <span className="text-text-muted">{t('pages.homepage.features.demo.tax.dividendIncome')}</span>
                        <strong className="text-large sm:text-[2.8rem]">{money(18420)}</strong>
                    </div>
                </div>
            </div>
            <div className={panel}>
                <div className="flex items-center justify-between gap-small">
                    <p className={caption}>{t('pages.homepage.features.demo.tax.incomeTaxLimit')}</p>
                    <strong className="text-[2.8rem]">62 %</strong>
                </div>
                <div className="my-auto pt-small text-vsmall text-text-muted">
                    <ProgressBar value={62} startValue={money(62000)} endValue={money(100000)} />
                </div>
            </div>
        </div>
    );
};

const WatchlistDemo: React.FC = () => {
    const { t } = useTranslation();
    const rows = [
        { symbol: 'AAPL', name: 'Apple', exchange: 'NASDAQ', price: '235,80 USD' },
        { symbol: 'MSFT', name: 'Microsoft', exchange: 'NASDAQ', price: '428,50 USD' },
        { symbol: 'CSPX', name: 'iShares Core S&P 500', exchange: 'LSE', price: '587,20 USD' },
        { symbol: 'NVDA', name: 'NVIDIA', exchange: 'NASDAQ', price: '138,40 USD' },
        { symbol: 'GOOGL', name: 'Alphabet', exchange: 'NASDAQ', price: '192,30 USD' },
    ];
    return (
        <div className={panel}>
            <div className="flex items-center justify-between gap-small border-b border-border pb-small">
                <p className="text-medium font-bold text-text">
                    {t('pages.homepage.features.demo.watchlist.listName')}
                </p>
                <span className="rounded-small bg-brand-primary/10 px-vsmall py-xxsmall text-vsmall font-bold text-brand-primary">
                    {rows.length}
                </span>
            </div>
            <div className="mt-small grid flex-1 content-stretch grid-cols-[minmax(0,1fr)_auto] gap-x-small text-small sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_auto]">
                <span className={caption}>{t('pages.homepage.features.demo.watchlist.asset')}</span>
                <span className={`${caption} hidden sm:block`}>
                    {t('pages.homepage.features.demo.watchlist.exchange')}
                </span>
                <span className={`${caption} text-right`}>{t('pages.homepage.features.demo.watchlist.close')}</span>
                {rows.map((row) => (
                    <React.Fragment key={row.symbol}>
                        <div className="min-w-0 border-t border-border py-vsmall">
                            <strong className="block text-text">{row.symbol}</strong>
                            <span className="block truncate text-vsmall text-text-muted">{row.name}</span>
                        </div>
                        <span className="hidden border-t border-border py-vsmall text-text-muted sm:block">
                            {row.exchange}
                        </span>
                        <strong className="border-t border-border py-vsmall text-right text-text">{row.price}</strong>
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

const AlertsDemo: React.FC = () => {
    const { t } = useTranslation();
    return (
        <div className={panel}>
            <div className="flex items-center justify-between gap-small border-b border-border pb-small">
                <div>
                    <p className={caption}>{t('pages.homepage.features.demo.alerts.symbolDetail')}</p>
                    <p className="mt-xxsmall text-medium font-bold text-text">Apple · AAPL</p>
                </div>
                <span className="rounded-full bg-brand-primary/10 p-vsmall text-brand-primary" aria-hidden="true">
                    <IconBell />
                </span>
            </div>
            <div className="mx-auto flex w-full max-w-[42rem] flex-1 flex-col justify-center py-medium text-center">
                <p className={caption}>{t('pages.homepage.features.demo.alerts.title')}</p>
                <p className="mt-small text-[3.2rem] font-bold leading-tight tracking-tight text-text sm:text-[5.2rem]">
                    250,00 USD
                </p>
                <p className="mt-vsmall text-small font-medium text-status-positive">+14,20 USD (+6,0 %)</p>
                <div className="mt-medium flex flex-wrap justify-center gap-[0.6rem] text-small text-text-muted">
                    {['+1 %', '+2 %', '+3 %', '+5 %', '+10 %'].map((step) => (
                        <span key={step} className="rounded-full border border-border px-[1.2rem] py-vsmall">
                            {step}
                        </span>
                    ))}
                </div>
                <p className="mt-medium border-t border-border pt-medium text-small text-text-muted">
                    {t('pages.homepage.features.demo.alerts.currentPrice')}: 235,80 USD
                </p>
            </div>
        </div>
    );
};

export const FeatureDemo: React.FC<FeatureDemoProps> = ({ feature }) => {
    switch (feature) {
        case 'performance':
            return <PerformanceDemo />;
        case 'dividends':
            return <DividendsDemo />;
        case 'allocation':
            return <AllocationDemo />;
        case 'tax':
            return <TaxDemo />;
        case 'watchlist':
            return <WatchlistDemo />;
        case 'alerts':
            return <AlertsDemo />;
        default:
            return null;
    }
};
